// Data hooks for the admin tabs. Every query is rejected server-side unless
// the session is in ADMIN_EMAILS, so errors here mean "not an admin".

import { useCallback } from "react";
import {
  AdminVendorFilter,
  useAdminReviewGoogleLinkMutation,
  useAdminSetAccountSuspendedMutation,
  useAdminExpiringVendorsQuery,
  useAdminSendExpiryReminderMutation,
  useAdminSetExpiryReminderMutation,
  useAdminSetVendorPausedMutation,
  useAdminSetVendorSuspendedMutation,
  useAdminApproveReverificationMutation,
  useAdminApproveVendorMutation,
  useAdminRejectVendorMutation,
  useAdminSummaryQuery,
  useAdminUsersQuery,
  useAdminVendorsQuery,
} from "generated/graphql";

import { PAGE_SIZE } from "./constants";
import { AdminActionResult, AdminUser, AdminVendor } from "./types";

interface MutationResult {
  error?: { message: string };
}

const toResult = (
  res: MutationResult,
  errors: { message?: string | null }[] | null | undefined,
  fallback: string,
): AdminActionResult => {
  if (errors?.length) {
    return { ok: false, error: errors[0].message ?? fallback };
  }
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
};

export const useAdminSummary = () => {
  const [{ data }, reexecute] = useAdminSummaryQuery({
    requestPolicy: "cache-and-network",
  });

  return {
    summary: data?.adminSummary,
    refresh: useCallback(
      () => reexecute({ requestPolicy: "network-only" }),
      [reexecute],
    ),
  };
};

export const useAdminVendors = (
  search: string,
  filter: AdminVendorFilter,
  page: number,
): {
  vendors: AdminVendor[];
  total: number;
  loading: boolean;
  error?: string;
  refresh: () => void;
} => {
  const [{ data, fetching, error }, reexecute] = useAdminVendorsQuery({
    variables: {
      search: search || null,
      filter,
      offset: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    },
    requestPolicy: "cache-and-network",
  });

  return {
    vendors: data?.adminVendors.items ?? [],
    total: data?.adminVendors.total ?? 0,
    loading: fetching && !data,
    error: error?.message,
    // An action can move a row out of the current filter.
    refresh: () => reexecute({ requestPolicy: "network-only" }),
  };
};

export const useAdminExpiringVendors = (): {
  vendors: AdminVendor[];
  loading: boolean;
  error?: string;
  refresh: () => void;
} => {
  const [{ data, fetching, error }, reexecute] = useAdminExpiringVendorsQuery({
    requestPolicy: "cache-and-network",
  });

  return {
    vendors: data?.adminExpiringVendors ?? [],
    loading: fetching && !data,
    error: error?.message,
    refresh: () => reexecute({ requestPolicy: "network-only" }),
  };
};

export const useAdminUsers = (
  search: string,
  suspendedOnly: boolean,
  page: number,
): {
  users: AdminUser[];
  total: number;
  loading: boolean;
  error?: string;
  refresh: () => void;
} => {
  const [{ data, fetching, error }, reexecute] = useAdminUsersQuery({
    variables: {
      search: search || null,
      suspendedOnly,
      offset: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    },
    requestPolicy: "cache-and-network",
  });

  return {
    users: data?.adminUsers.items ?? [],
    total: data?.adminUsers.total ?? 0,
    loading: fetching && !data,
    error: error?.message,
    // An action can move a row out of the current filter.
    refresh: () => reexecute({ requestPolicy: "network-only" }),
  };
};

// Results are normalized by vendor/account id, so lists update in place.
export const useAdminActions = () => {
  const [, approveMut] = useAdminApproveVendorMutation();
  const [, rejectMut] = useAdminRejectVendorMutation();
  const [, reverifyMut] = useAdminApproveReverificationMutation();
  const [, pauseMut] = useAdminSetVendorPausedMutation();
  const [, sendReminderMut] = useAdminSendExpiryReminderMutation();
  const [, reminderMut] = useAdminSetExpiryReminderMutation();
  const [, reviewMut] = useAdminReviewGoogleLinkMutation();
  const [, vendorSuspendMut] = useAdminSetVendorSuspendedMutation();
  const [, accountSuspendMut] = useAdminSetAccountSuspendedMutation();

  const approveVendor = useCallback(
    async (vendorId: number) => {
      const res = await approveMut({ vendorId });
      return toResult(
        res,
        res.data?.adminApproveVendor.errors,
        "Failed to approve vendor",
      );
    },
    [approveMut],
  );

  const rejectVendor = useCallback(
    async (vendorId: number, reason: string) => {
      const res = await rejectMut({ vendorId, reason });
      return toResult(
        res,
        res.data?.adminRejectVendor.errors,
        "Failed to reject vendor",
      );
    },
    [rejectMut],
  );

  const approveReverification = useCallback(
    async (vendorId: number) => {
      const res = await reverifyMut({ vendorId });
      return toResult(
        res,
        res.data?.adminApproveReverification.errors,
        "Failed to approve changes",
      );
    },
    [reverifyMut],
  );

  const setVendorPaused = useCallback(
    async (vendorId: number, paused: boolean, reason?: string) => {
      const res = await pauseMut({ vendorId, paused, reason: reason ?? null });
      return toResult(
        res,
        res.data?.adminSetVendorPaused.errors,
        "Failed to update vendor",
      );
    },
    [pauseMut],
  );

  const sendExpiryReminder = useCallback(
    async (vendorId: number) => {
      const res = await sendReminderMut({ vendorId });
      return toResult(
        res,
        res.data?.adminSendExpiryReminder.errors,
        "Failed to send reminder",
      );
    },
    [sendReminderMut],
  );

  const setExpiryReminder = useCallback(
    async (vendorId: number, enabled: boolean) => {
      const res = await reminderMut({ vendorId, enabled });
      return toResult(
        res,
        res.data?.adminSetExpiryReminder.errors,
        "Failed to update reminder",
      );
    },
    [reminderMut],
  );

  const reviewGoogleLink = useCallback(
    async (vendorId: number, approve: boolean) => {
      const res = await reviewMut({ vendorId, approve });
      return toResult(
        res,
        res.data?.adminReviewGoogleLink.errors,
        "Failed to review link",
      );
    },
    [reviewMut],
  );

  const setVendorSuspended = useCallback(
    async (vendorId: number, suspended: boolean) => {
      const res = await vendorSuspendMut({ vendorId, suspended });
      return toResult(
        res,
        res.data?.adminSetVendorSuspended.errors,
        "Failed to update vendor",
      );
    },
    [vendorSuspendMut],
  );

  const setAccountSuspended = useCallback(
    async (accountId: number, suspended: boolean) => {
      const res = await accountSuspendMut({ accountId, suspended });
      return toResult(
        res,
        res.data?.adminSetAccountSuspended.errors,
        "Failed to update account",
      );
    },
    [accountSuspendMut],
  );

  return {
    approveVendor,
    rejectVendor,
    approveReverification,
    setVendorPaused,
    sendExpiryReminder,
    setExpiryReminder,
    reviewGoogleLink,
    setVendorSuspended,
    setAccountSuspended,
  };
};
